import React, { useEffect, useRef, useState } from "react";
import { FaBarcode, FaCheckCircle, FaIdCard, FaUser } from "react-icons/fa";
import { FiArrowLeft, FiCalendar, FiClock, FiMapPin, FiSearch } from "react-icons/fi";
import { IoCheckmark } from "react-icons/io5";
import moment from "moment";
import { Button, Input } from "reactstrap";
import {
  applyClassToCharacters,
  getStringFromJSX,
  LoadingSpinner,
} from "../../constants/const";
import { GetSelectedLanguage } from "../helper/Method";
import { addMember } from "../member/ApiMember";
import { RxCross2 } from "react-icons/rx";
import { GetLanguageString } from "../helper/Components";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  withRouter,
} from "react-router-dom/cjs/react-router-dom.min";
import { ApiEvent } from "./ApiEvent";
import "./barCodeAttendance.css";

const EventBarCodeAttendance = ({
  history,
  eventIdFromToken,
  applyMarginTop = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scannedCode, setScannedCode] = useState("");
  const [scannedCodeForError, setScannedCodeForError] = useState("");
  const [loading, setLoading] = useState(false);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [unMarkingAttendance, setUnMarkingAttendance] = useState(false);
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);
  const [markingAttendanceSuccess, setMarkingAttendanceSuccess] = useState("");
  const [markingAttendanceError, setMarkingAttendanceError] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [scanStatus, setScanStatus] = useState("ready"); // 'ready', 'scanning', 'success', 'error'
  const [error, setError] = useState(false);
  const inputBufferRef = useRef(""); // Buffer for barcode scanning
  const eventId = eventIdFromToken ? eventIdFromToken : location.state?.eventId;
  const eventType = location.state?.eventType;
  const eventAddress = location.state?.eventAddress;
  const eventDate = location.state?.eventDate;

  const fetchMemberDetails = (code) => {
    if (!code) return;
    setLoading(true);
    setError(false);
    setMarkingAttendanceError("");
    setMarkingAttendanceSuccess("");
    setIsAttendanceMarked(false);
    setScannedCodeForError(code);

    addMember()
      .GetMemberDetailsBySerialNumberForEventAttendance(code, eventId)
      .then((res) => {
        setMemberData({
          ...res.data,
          memberPhoto: res.data.memberPhoto
            ? "data:image/jpeg;base64," + res.data.memberPhoto
            : null,
        });

        const message = getStringFromJSX(
          <GetLanguageString
            props="member_member_rafiq_ex"
            value={res.data.fullName}
          />
        );

        // Apply character classes if needed
        const formattedMessage = res.data.isExRafiq
          ? getStringFromJSX(applyClassToCharacters(message, "ur"))
          : null;

        // Set the error
        if (formattedMessage) {
          setMarkingAttendanceError(formattedMessage);
        }

        if (res.data.attendanceExists) {
          setScanStatus("ready");
          setMarkingAttendanceError("Error: " + t("already_marked_attendance"));
        } else {
          setScanStatus("success");
        }

        setScannedCode(""); // Clear the input field on error
        setLoading(false);
      })
      .catch((err) => {
        setError(true);

        let serverMessage = "event_attendance_barcode_error_marking_attendance";
        let errorType = "event_attendance_barcode_error_marking_attendance";

        if (err.response) {
          if (err.response.data?.message) {
            serverMessage = err.response.data.message;
          } else if (typeof err.response.data === "string") {
            serverMessage = err.response.data;
          }

          if (err.response.data?.type) {
            errorType = err.response.data.type;
          } else if (typeof err.response.data === "string") {
            errorType = err.response.data;
          }

          // Handle 500 internal error gracefully
          if (err.response.status === 500) {
            serverMessage =
              "Serial number might be incorrect or record doesn't exist.";
          }
        }
        if (
          serverMessage.includes("already_marked_attendance") ||
          errorType.includes("already_marked_attendance")
        ) {
          setScanStatus("");
        } else {
          setScanStatus("error");
        }
        setMarkingAttendanceError("Error: " + t(serverMessage));

        setMemberData("");
        setMarkingAttendance(false);
        setLoading(false);
        setScannedCode(""); // Clear the input field on error
      });
  };

  // Update your useEffect for barcode scanning
  useEffect(() => {
    let timer = null;

    const handleKeyPress = (e) => {
      // Ignore if input field is focused
      if (document.activeElement.tagName === "INPUT") return;

      if (e.key === "Enter") {
        if (inputBufferRef.current) {
          fetchMemberDetails(inputBufferRef.current);
          inputBufferRef.current = "";
        }
        return;
      }

      inputBufferRef.current += e.key;

      clearTimeout(timer);
      timer = setTimeout(() => {
        inputBufferRef.current = "";
        setScanStatus("ready");
      }, 100); // Reduced timeout for better performance
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => window.removeEventListener("keypress", handleKeyPress);
    // eslint-disable-next-line
  }, []);

  // Update your manual input handling
  const handleManualInput = (e) => {
    if (scanStatus !== "success") setScannedCode(e.target.value);
  };

  const handleManualSubmit = (e) => {
    if (e.key === "Enter" && e.target.value.trim() !== "") {
      // Clear the buffer to prevent duplicate scans
      inputBufferRef.current = "";
      fetchMemberDetails(e.target.value);
    }
  };

  const getStatusIcon = () => {
    switch (scanStatus) {
      case "scanning":
        return <FaBarcode size={64} className="animate-pulse" />;
      case "success":
        return <FaCheckCircle size={64} className="animate-pulse" />;
      case "error":
        return <FaBarcode size={64} className="animate-pulse" />;
      default:
        return <FaBarcode size={64} className="animate-pulse" />;
    }
  };

  const handleMarkAttendance = (e) => {
    e.preventDefault();
    setLoading(true);
    setMarkingAttendance(true);
    setMarkingAttendanceError("");
    setMarkingAttendanceSuccess("");
    setIsAttendanceMarked(false);
    ApiEvent()
      .addeventparticipant({
        eventId: eventId,
        serialNumber: memberData.serialNumber.toString(),
        onTime: true,
        memberId: null,
      })
      .then((res) => {
        if (res.data) {
          setLoading(false);
          setMemberData("");
          setMarkingAttendanceError("");
          setScannedCode("");
          setScanStatus("ready");
          setIsAttendanceMarked(true);
          setMarkingAttendance(false);
          setMarkingAttendanceSuccess(
            GetSelectedLanguage() === "ur"
              ? res.data + " کی حاضری لگا دی گئی ہے"
              : "Attendance marked for " + res.data
          );
        }
      })
      .catch((err) => {
        setMemberData("");
        setLoading(false);
        setMarkingAttendance(false);
        setScannedCode("");
        let serverMessage = "event_attendance_barcode_error_marking_attendance";
        let errorType = "event_attendance_barcode_error_marking_attendance";

        if (err.response) {
          if (err.response.data?.message) {
            serverMessage = err.response.data.message;
          } else if (typeof err.response.data === "string") {
            serverMessage = err.response.data;
          }

          if (err.response.data?.type) {
            errorType = err.response.data.type;
          } else if (typeof err.response.data === "string") {
            errorType = err.response.data;
          }

          if (err.response.status === 500) {
            setScanStatus("error");
            setMarkingAttendanceError(
              "Serial number might be incorrect or record doesn't exist."
            );
            return;
          }
        }

        if (
          serverMessage.includes("already_marked_attendance") ||
          errorType.includes("already_marked_attendance")
        ) {
          setScanStatus("");
        } else {
          setScanStatus("error");
        }

        setMarkingAttendanceError("Error: " + t(serverMessage));

        if (errorType.includes("invalid_event_participant")) {
          setScanStatus("");
          setMarkingAttendanceError(
            GetSelectedLanguage() === "ur"
              ? +"یہ رفیق " +
                  serverMessage +
                  " اس اجتماع میں شامل ہونے کی اجازت نہیں رکھتا"
              : "This rafiq " + serverMessage + " is not allowed in this Ijtima"
          );
        }
      });
  };

  const deleteEventParticipant = (memberId) => {
    setLoading(true);
    setUnMarkingAttendance(true);
    setMarkingAttendanceError("");
    ApiEvent()
      .deleteEventParticipant(memberId, eventId)
      .then((res) => {
        setLoading(false);
        setMemberData("");
        setMarkingAttendanceError("");
        setScannedCode("");
        setScanStatus("ready");
        setIsAttendanceMarked(true);
        setUnMarkingAttendance(false);
        setMarkingAttendanceSuccess(
          GetSelectedLanguage() === "ur"
            ? "حاضری منسوخ کر دی گئی ہے۔"
            : "Attendance has been unmarked."
        );
      })
      .catch((err) => {
        setMarkingAttendanceError(err.response.data);
        setLoading(false);
        setScanStatus("");
        setMemberData("");
        setUnMarkingAttendance(false);
        setScannedCode("");
      });
  };

  const isUrduLang = GetSelectedLanguage() === "ur";
  const unitParts = (memberData?.unitName || "")
    .split("،")
    .map((part) => part.trim())
    .filter(Boolean);
  const scanTitle =
    scanStatus === "scanning"
      ? t("event_attendance_barcode_scanning")
      : scanStatus === "success"
      ? t("event_attendance_barcode_member_verified")
      : scanStatus === "error"
      ? t("event_attendance_barcode_scan_failed")
      : t("event_attendance_barcode_scan");
  const scanHint =
    scanStatus === "ready"
      ? t("event_attendance_barcode_scan_point_scanner")
      : scanStatus === "scanning"
      ? t("event_attendance_barcode_reading")
      : scanStatus === "success"
      ? t("event_attendance_barcode_confirm_attendance")
      : t("please_try_again");
  const resetScan = () => {
    setMemberData("");
    setScanStatus("ready");
    setScannedCode("");
    setMarkingAttendanceError("");
    setMarkingAttendanceSuccess("");
    setIsAttendanceMarked(false);
  };

  return (
    <div
      className={
        applyMarginTop
          ? "wrapper-custom wrapper wrapper-content animated fadeInRight bc-page"
          : "bc-page"
      }
    >
      {eventIdFromToken ? null : (
        <div className="bc-hero">
          <div className="bc-hero-main">
            <div className="bc-hero-label">
              <GetLanguageString props="events_attendance_mark_by_bar_code" />
            </div>
            {eventType ? (
              <h1 className="bc-hero-title">
                {applyClassToCharacters(eventType, "ur")}
              </h1>
            ) : null}
            {eventAddress || eventDate ? (
              <div className="bc-hero-meta">
                {eventAddress ? (
                  <span className="bc-chip">
                    <FiMapPin />
                    <span>{applyClassToCharacters(eventAddress, "ur")}</span>
                  </span>
                ) : null}
                {eventDate ? (
                  <span className="bc-chip">
                    <FiCalendar />
                    <span>
                      {moment
                        .utc(eventDate)
                        .locale(GetSelectedLanguage())
                        .format("DD/MM/yyyy    h:mm a")}
                    </span>
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          {eventIdFromToken ? null : (
            <button
              type="button"
              className="bc-hero-btn"
              onClick={() => history.goBack()}
            >
              <FiArrowLeft />
              <GetLanguageString props="courses_participants_back" />
            </button>
          )}
        </div>
      )}

      <div className="bc-grid">
        <section className={`bc-scan bc-scan-${scanStatus || "idle"}`}>
          {!loading ? (
            <>
              <div className="bc-scan-ring">{getStatusIcon()}</div>
              <h3 className="bc-scan-title">{scanTitle}</h3>
              <p className="bc-scan-hint">{scanHint}</p>
              {scanStatus === "success" ? null : (
                <>
                  <span className="bc-or">{isUrduLang ? "یا" : "or"}</span>
                  <div className="bc-input-wrap">
                    <FiSearch className="bc-input-icon" />
                    <Input
                      type="text"
                      autoFocus
                      value={scannedCode}
                      onChange={handleManualInput}
                      onKeyDown={handleManualSubmit}
                      className="bc-input"
                      placeholder={
                        GetSelectedLanguage() === "en"
                          ? "Enter Serial Number"
                          : "سیریل نمبر لکھیں "
                      }
                    />
                    <button
                      type="button"
                      className="bc-find"
                      onClick={() => {
                        if (scannedCode.trim() !== "") {
                          inputBufferRef.current = "";
                          fetchMemberDetails(scannedCode);
                        }
                      }}
                    >
                      <GetLanguageString props="events_barcode_find" />
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="bc-spinner">{LoadingSpinner()}</div>
              <h3 className="bc-scan-title">
                <GetLanguageString
                  props={
                    markingAttendance
                      ? "event_attendance_barcode_marking_attendance"
                      : unMarkingAttendance
                      ? "event_attendance_barcode_unmarking_attendance"
                      : "event_attendance_barcode_fetching"
                  }
                />
              </h3>
            </>
          )}
        </section>

        <section className="bc-member">
          {!loading ? (
            memberData ? (
              <>
                <div className="bc-member-head">
                  <FaIdCard size={20} />
                  <span>
                    <GetLanguageString props="member_member_detail" />
                  </span>
                </div>
                <div className="bc-member-body">
                  <div className="bc-member-top">
                    <div className="bc-photo">
                      {memberData.memberPhoto ? (
                        <img src={memberData.memberPhoto} alt="" />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                    <div className="bc-member-name">
                      <h2>{applyClassToCharacters(memberData.fullName, "ur")}</h2>
                      <div className="bc-badges">
                        {memberData.memberType ? (
                          <span className="bc-badge bc-badge-type">
                            {applyClassToCharacters(memberData.memberType, "ur")}
                          </span>
                        ) : null}
                        <span
                          className={
                            memberData.attendanceExists
                              ? "bc-badge bc-badge-ok"
                              : "bc-badge bc-badge-wait"
                          }
                        >
                          <GetLanguageString
                            props={
                              memberData.attendanceExists
                                ? "events_barcode_attendance_marked"
                                : "events_barcode_attendance_not_marked"
                            }
                          />
                        </span>
                        {memberData.isExRafiq ? (
                          <span className="bc-badge bc-badge-bad">
                            {isUrduLang ? "سابق رفیق" : "Ex rafiq"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <dl className="bc-facts">
                    <div>
                      <dt>
                        <GetLanguageString props="member_reg_number" />
                      </dt>
                      <dd className="bc-en">{memberData.regNo}</dd>
                    </div>
                    <div>
                      <dt>
                        <GetLanguageString props="events_barcode_serial_number" />
                      </dt>
                      <dd className="bc-en">{memberData.serialNumber}</dd>
                    </div>
                    {memberData?.responsibilityName?.trim() ? (
                      <div className="bc-wide">
                        <dt>
                          <GetLanguageString props="member_responsibility" />
                        </dt>
                        <dd>
                          {applyClassToCharacters(
                            memberData.responsibilityName.trim(),
                            "ur"
                          )}
                        </dd>
                      </div>
                    ) : null}
                    <div className="bc-wide">
                      <dt>
                        <GetLanguageString props="unit" />
                      </dt>
                      <dd>
                        <ol className="bc-path">
                          {unitParts.map((item, index) => (
                            <li key={index}>
                              {applyClassToCharacters(item, "ur")}
                            </li>
                          ))}
                        </ol>
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="bc-member-foot">
                  <small className="bc-time">
                    <FiClock /> {t("event_attendance_barcode_scanned_at")}{" "}
                    {new Date().toLocaleTimeString()}
                  </small>
                  <div className="bc-actions">
                    <Button outline color="danger" onClick={resetScan}>
                      <RxCross2 size={16} />{" "}
                      <GetLanguageString props={"common_cancel"} />
                    </Button>
                    {memberData.attendanceExists ? null : (
                      <Button
                        color="success"
                        disabled={memberData ? memberData.isExRafiq : false}
                        onClick={handleMarkAttendance}
                      >
                        <IoCheckmark size={16} />{" "}
                        <GetLanguageString props={"common_confirm"} />
                      </Button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bc-empty">
                <FaBarcode size={56} />
                <h5>
                  <GetLanguageString
                    props={"event_attendance_barcode_scan_no_scanned"}
                  />
                </h5>
                <p>
                  <GetLanguageString
                    props={"event_attendance_barcode_scan_to_show_details"}
                  />
                </p>
              </div>
            )
          ) : (
            <div className="bc-empty">{LoadingSpinner()}</div>
          )}
        </section>
      </div>

      {error && !markingAttendanceError ? (
        <div className="bc-banner bc-banner-error">
          <GetLanguageString
            props={"event_attendance_barcode_scan_error"}
            value={scannedCodeForError}
          />
        </div>
      ) : null}
      {markingAttendanceError ? (
        <div className="bc-banner bc-banner-error">
          <span>{applyClassToCharacters(markingAttendanceError, "ur")}</span>
          {markingAttendanceError.includes(
            "Attendance is already marked for this rafiq" ||
              markingAttendanceError.includes(
                " اس رفیق کی حاضری پہلے ہی لگ چکی ہے"
              )
          ) ? (
            <Button
              color="danger"
              size="sm"
              className="ms-3"
              onClick={() => deleteEventParticipant(memberData.id)}
              style={{ fontWeight: 500 }}
            >
              ✕{" "}
              <GetLanguageString props={"events_attendance_unmark_attendance"} />
            </Button>
          ) : null}
        </div>
      ) : null}
      {isAttendanceMarked ? (
        <div className="bc-banner bc-banner-success">
          <span>{applyClassToCharacters(markingAttendanceSuccess, "ur")}</span>
        </div>
      ) : null}

    </div>
  );
};

export default withRouter(EventBarCodeAttendance);
